// Insurance claims — presentational vertical status timeline.
// Filed→Acknowledged→Adjusting→Settled|Denied. Reached stages show a
// date stamp; the current (non-terminal) stage pulses with a gold accent.

import { cn } from "@/design-system/lib/utils";
import { formatDate } from "../common/colorBand";
import { stageDateOf } from "./claimLifecycle";
import type {
  InsuranceClaimDto,
  InsuranceClaimStatus,
} from "@/api/portfolioAdmin/insuranceClaimsApi";

interface ClaimStatusTimelineProps {
  claim: InsuranceClaimDto;
}

function stagesFor(claim: InsuranceClaimDto): InsuranceClaimStatus[] {
  // The terminal slot renders Denied only when the claim was denied;
  // otherwise it shows the Settled stage (reached or pending).
  return claim.status === "Denied"
    ? ["Filed", "Acknowledged", "Adjusting", "Denied"]
    : ["Filed", "Acknowledged", "Adjusting", "Settled"];
}

export function ClaimStatusTimeline({ claim }: ClaimStatusTimelineProps) {
  const stages = stagesFor(claim);
  const terminal = claim.status === "Settled" || claim.status === "Denied";

  return (
    <ol
      className="m-0 p-0 list-none flex flex-col"
      data-testid="claim-status-timeline"
    >
      {stages.map((stage, idx) => {
        const date = stageDateOf(claim, stage);
        const reached = !!date;
        const isCurrent = stage === claim.status;
        const isDenied = stage === "Denied";
        const isLast = idx === stages.length - 1;
        return (
          <li key={stage} className="flex gap-space-3">
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  "h-3 w-3 rounded-full border-2 shrink-0 mt-[3px]",
                  isCurrent && !terminal
                    ? "border-gold bg-gold motion-safe:animate-pulse"
                    : reached
                      ? isDenied
                        ? "border-status-error bg-status-error"
                        : "border-status-success bg-status-success"
                      : "border-border-light bg-white-pure",
                )}
                aria-hidden
              />
              {!isLast && (
                <span
                  className={cn(
                    "w-px flex-1 min-h-space-4",
                    reached ? "bg-gray-mid" : "bg-border-light",
                  )}
                  aria-hidden
                />
              )}
            </div>
            <div className="pb-space-3">
              <div
                className={cn(
                  "text-small font-display",
                  isCurrent
                    ? "font-bold text-onyx"
                    : reached
                      ? "font-semibold text-onyx"
                      : "text-gray-mid",
                )}
              >
                {stage}
                {isCurrent && !terminal && (
                  <span className="ml-space-2 text-caption text-gold-shadow font-semibold uppercase tracking-label">
                    Current
                  </span>
                )}
              </div>
              <div className="text-caption text-gray-mid">
                {reached ? formatDate(date) : "Not reached"}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
